var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { VxIcon } from "../../assets";
import { IconButton } from "../../components";
import { ChakraButton, Flex, ChakraTag, Icon } from "../../ui";
/**
 * Tag component is used for items that need to be labeled, categorized, or organized using keywords that describe them.
 * Tag composes and `Chakra's Tag component` and `Chakra's Box component`, so you can pass all its props.
 *
 * See more on  **Chakra API** or directly in [Chakra's documentation](https://chakra-ui.com/docs/components/tag/usage)
 *
 * ```tsx
 * import { Tag, TagProps } from '@springcare/sh-component-library'
 *
 * <Tag icon={VMomentsIcon} onClick={()=>console.log("click")}/>
 * <Tag icon={VMomentsIcon} onRemove={()=>console.log("remove")}/>
 * ```
 *
 * ## Verdant API (props)
 * - [Verdant's Tag specification](https://www.figma.com/file/ymjrEQ8Mn8OebdtoNPhXD0/%F0%9F%8D%83-Tag)
 *
 *
 * ### icon
 * - Off (icon undefined)
 * - On (icon defined, see [SVG Icon Reference](https://fantastic-broccoli-de639a7d.pages.github.io/?path=/docs/foundations-icons-icon-gallery--page))
 *
 * <br>
 *
 *<details>
 *  <summary>**Chakra API**</summary>
 *  <br>
 *  Tag composes and `Chakra's Tag component` and `Chakra's Box props or Style Props`
 *  - [Chakra Tag Props](https://chakra-ui.com/docs/components/tag/props)
 *  - [Chakra Style Props](https://chakra-ui.com/docs/styled-system/style-props)
 * </details>
 *
 * @override
 * @param {TagProps} props avatar props
 * @author Alejandro De la Cruz Tarin <adelacruz.ctr@springhealth.com>
 * @returns {React.ReactElement} Badge
 * @see _avatarTheme button theme definition using chakra
 * @version 1.0.1
 */
export var Tag = React.forwardRef(function (_a, ref) {
    var icon = _a.icon, children = _a.children, _b = _a.size, size = _b === void 0 ? "3xs" : _b, _c = _a.variant, variant = _c === void 0 ? "medium-emphasis" : _c, isDisabled = _a.isDisabled, isLoading = _a.isLoading, selected = _a.selected, tagProps = __rest(_a, ["icon", "children", "size", "variant", "isDisabled", "isLoading", "selected"]);
    var content = (_jsxs(Flex, __assign({ as: "span", px: "v-2", alignItems: "center", gap: "v-4" }, { children: [icon ? _jsx(Icon, { as: icon, boxSize: "14px" }) : null, children] })));
    if ("onRemove" in tagProps) {
        var onRemove = tagProps.onRemove, props = __rest(tagProps, ["onRemove"]);
        return (_jsxs(ChakraTag, __assign({}, props, { variant: "v-".concat(variant), size: "v-".concat(size), ref: ref }, { children: [content, !isDisabled ? (_jsx(IconButton, { variant: variant, size: size, icon: VxIcon, isLoading: isLoading, isDisabled: isDisabled, selected: selected, onClick: onRemove, "aria-label": "Close Button" })) : null] })));
    }
    return (_jsx(ChakraButton, __assign({}, tagProps, { iconSpacing: "v-2", variant: "v-".concat(variant, "-tag"), size: "v-".concat(size, "-tag"), isLoading: isLoading, isDisabled: isDisabled, selected: selected, ref: ref }, { children: content })));
});
